import type { StoreSettings } from '@/types'

const LAUNCH_SEEN_PREFIX = 'ooud-kraft-launch-seen'

export function parseLaunchAt(value: string | undefined): number | null {
  if (!value?.trim()) return null
  const ms = Date.parse(value.trim())
  return Number.isFinite(ms) ? ms : null
}

export function getLaunchSeenStorageKey(launchAt: string): string {
  return `${LAUNCH_SEEN_PREFIX}:${launchAt.trim()}`
}

export function hasCompletedLaunchCelebration(launchAt: string): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(getLaunchSeenStorageKey(launchAt)) === '1'
}

export function markLaunchCelebrationComplete(launchAt: string): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(getLaunchSeenStorageKey(launchAt), '1')
}

export function isLaunchGateConfigured(
  settings: Pick<StoreSettings, 'launchGateEnabled' | 'launchAt'> | null | undefined
): boolean {
  return (
    settings?.launchGateEnabled === true &&
    parseLaunchAt(settings.launchAt) != null
  )
}

/** Storefront visitors who still need the pre-launch countdown page. */
export function shouldRedirectToLaunchPage(
  settings: Pick<StoreSettings, 'launchGateEnabled' | 'launchAt'> | null | undefined
): boolean {
  if (!isLaunchGateConfigured(settings)) return false

  const launchAt = settings!.launchAt!.trim()
  const launchAtMs = parseLaunchAt(launchAt)
  if (launchAtMs == null) return false

  // After launch time the store is open — no launch page for new visitors.
  if (Date.now() >= launchAtMs) return false

  return !hasCompletedLaunchCelebration(launchAt)
}

/** True when launch time has passed (store is considered live). */
export function isStoreLive(
  settings: Pick<StoreSettings, 'launchAt'> | null | undefined
): boolean {
  const launchAtMs = parseLaunchAt(settings?.launchAt)
  return launchAtMs != null && Date.now() >= launchAtMs
}

export function formatCountdownParts(ms: number) {
  const totalSec = Math.max(0, Math.floor(ms / 1000))
  return {
    days: Math.floor(totalSec / 86400),
    hours: Math.floor((totalSec % 86400) / 3600),
    minutes: Math.floor((totalSec % 3600) / 60),
    seconds: totalSec % 60,
  }
}

/** `datetime-local` input value from an ISO string (browser local time). */
export function isoToDatetimeLocal(iso: string | undefined): string {
  if (!iso?.trim()) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** ISO string from a `datetime-local` input value. */
export function datetimeLocalToIso(value: string): string {
  if (!value.trim()) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString()
}
